import { USE_OPS_MOCKS } from 'pages/Operations/Shared/opsControl'
import {
  IInventoryDemoReceiptRow,
  IInventoryDemoSupplierRow,
  IInventoryReceiptQc,
  IInventorySupplierCommercials,
} from 'types/inventorySupply.interface'
import { IStatusPill } from 'types/ui/statusPill.interface'

export const formatSupplyGhs = (value: number): string =>
  'GH₵ ' + new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value)

const DEMO_SUPPLIERS: IInventoryDemoSupplierRow[] = [
  {
    id: 9101,
    name: 'Mansa Industrial Supplies',
    phoneNumber: '+233 24 555 0117',
    address: 'Plot 6, Heavy Industrial Area, Tarkwa',
    vatId: 'GHA-402-8817',
    email: 'sales@mansaindustrial.com',
    status: 1,
  },
  {
    id: 9102,
    name: 'TyreCo West Africa',
    phoneNumber: '+233 30 277 4410',
    address: 'Spintex Rd, Accra',
    vatId: 'GHA-556-1092',
    email: 'orders@tyrecowa.com',
    status: 1,
  },
  {
    id: 9103,
    name: 'FluidPower Ghana',
    phoneNumber: '+233 24 811 2263',
    address: 'Ring Road West, Takoradi',
    vatId: 'GHA-233-6741',
    email: 'supply@fluidpowergh.com',
    status: 1,
  },
  {
    id: 9104,
    name: 'BeltTech Ltd',
    phoneNumber: '+233 20 944 8351',
    address: 'Kaase Industrial Estate, Kumasi',
    vatId: 'GHA-871-3305',
    email: 'accounts@belttech.com',
    status: 1,
  },
]

const DEMO_SUPPLIER_COMMERCIALS: IInventorySupplierCommercials[] = [
  {
    supplierName: 'Mansa Industrial Supplies',
    avgLeadTimeDays: 9,
    promisedLeadTimeDays: 7,
    onTimePct: 92,
    lastPriceMoveLabel: '+4% May',
    openOrdersCount: 2,
    openOrdersValueGhs: 18600,
  },
  {
    supplierName: 'TyreCo West Africa',
    avgLeadTimeDays: 41,
    promisedLeadTimeDays: 35,
    onTimePct: 78,
    lastPriceMoveLabel: '+6% Apr',
    openOrdersCount: 1,
    openOrdersValueGhs: 56800,
    note: 'tyres often late — buffer stock',
  },
  {
    supplierName: 'FluidPower Ghana',
    avgLeadTimeDays: 8,
    promisedLeadTimeDays: 8,
    onTimePct: 95,
    lastPriceMoveLabel: '−2% Jun',
    openOrdersCount: 1,
    openOrdersValueGhs: 12600,
  },
  {
    supplierName: 'BeltTech Ltd',
    avgLeadTimeDays: 44,
    promisedLeadTimeDays: 30,
    onTimePct: 66,
    lastPriceMoveLabel: '+9% Mar',
    openOrdersCount: 1,
    openOrdersValueGhs: 46000,
    note: 'belt-roll stockout traced to this lead time',
  },
]

export const INVENTORY_DEMO_SUPPLIERS: IInventoryDemoSupplierRow[] = USE_OPS_MOCKS
  ? DEMO_SUPPLIERS
  : []

export const getInventorySupplierCommercials = (
  name: string,
  index: number,
): IInventorySupplierCommercials | null => {
  if (!USE_OPS_MOCKS) return null
  const byName = DEMO_SUPPLIER_COMMERCIALS.find((c) => c.supplierName === name)
  if (byName) return byName
  return index >= 0
    ? DEMO_SUPPLIER_COMMERCIALS[index % DEMO_SUPPLIER_COMMERCIALS.length]
    : null
}

export const supplierLeadDeltaDays = (c: IInventorySupplierCommercials): number =>
  c.avgLeadTimeDays - c.promisedLeadTimeDays

export const supplierLeadDeltaTone = (c: IInventorySupplierCommercials): IStatusPill['status'] => {
  const delta = supplierLeadDeltaDays(c)
  if (delta <= 0) return 'success'
  return delta >= 10 ? 'danger' : 'warning'
}

export const supplierOnTimeTone = (pct: number): IStatusPill['status'] =>
  pct >= 90 ? 'success' : pct >= 75 ? 'warning' : 'danger'

export const INVENTORY_SUPPLIER_FOOTNOTE =
  'Lead-time reality feeds reorder timing on the dashboard.'

export const RECEIPT_QC_META: {
  [k in IInventoryReceiptQc['result']]: { label: string; tone: IStatusPill['status'] }
} = {
  PASS: { label: 'Pass', tone: 'success' },
  PENDING: { label: 'Pending', tone: 'warning' },
  REJECTED: { label: 'Rejected', tone: 'danger' },
}

const DEMO_RECEIPTS: IInventoryDemoReceiptRow[] = [
  {
    id: 'demo-rcp-313',
    supplier: { name: 'TyreCo West Africa' },
    location: { name: 'Main warehouse — Tarkwa' },
    receiptNo: 'RCP-313',
    receiptDate: '2026-07-04',
    status: 1,
    currency: 'GH₵',
    totalAmount: 46200,
    itemsLabel: 'Haul truck tyre 27.00R49 × 2 (TYR-2749)',
    qc: {
      checkedBy: 'B. Osei — tyre fitter',
      result: 'PENDING',
      reason: 'awaiting fitter inspection',
      checkedAt: 'due 6 Jul',
    },
    isDemo: true,
  },
  {
    id: 'demo-rcp-312',
    supplier: { name: 'FluidPower Ghana' },
    location: { name: 'Workshop store' },
    receiptNo: 'RCP-312',
    receiptDate: '2026-07-02',
    status: 2,
    currency: 'GH₵',
    totalAmount: 37800,
    itemsLabel: 'Hydraulic oil 208 L drum × 12 (OIL-HYD208)',
    qc: {
      checkedBy: 'K. Asante — workshop lead',
      result: 'PASS',
      checkedAt: '2 Jul, 09:40',
    },
    isDemo: true,
  },
  {
    id: 'demo-rcp-311',
    supplier: { name: 'Mansa Industrial Supplies' },
    location: { name: 'Main warehouse — Tarkwa' },
    receiptNo: 'RCP-311',
    receiptDate: '2026-06-30',
    status: 2,
    currency: 'GH₵',
    totalAmount: 12400,
    itemsLabel: 'Oil filters — CAT 777 & consumables (FLT-0917)',
    qc: {
      checkedBy: 'A. Mensah — storekeeper',
      result: 'PASS',
      checkedAt: '30 Jun, 14:10',
    },
    isDemo: true,
  },
  {
    id: 'demo-rcp-309',
    supplier: { name: 'BeltTech Ltd' },
    location: { name: 'Plant store — crushing' },
    receiptNo: 'RCP-309',
    receiptDate: '2026-06-26',
    status: 2,
    currency: 'GH₵',
    totalAmount: 46000,
    itemsLabel: 'Conveyor belt roll 800 mm × 1 (BLT-0800)',
    qc: {
      checkedBy: 'E. Boateng — maintenance planner',
      result: 'REJECTED',
      reason: 'belt roll edge damage — return raised',
      checkedAt: '26 Jun, 16:05',
    },
    discrepancy: {
      orderedQty: 2,
      receivedQty: 1,
      deltaQty: -1,
      note: 'short 1 — claim with supplier',
    },
    isDemo: true,
  },
]

export const INVENTORY_DEMO_RECEIPTS: IInventoryDemoReceiptRow[] = USE_OPS_MOCKS
  ? DEMO_RECEIPTS
  : []

export const INVENTORY_RECEIPTS_WEEK_TOTAL_GHS = DEMO_RECEIPTS.filter(
  (r) => r.receiptDate >= '2026-06-29',
).reduce((sum, r) => sum + r.totalAmount, 0)

export const INVENTORY_RECEIPT_QC_CAPTION =
  'QC and discrepancy are captured at receiving — rejected goods never post silently.'
